import { useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Users, Phone, Upload, X, FileImage } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LICENSE_TYPES = [
  { value: 'Code 8',  label: 'Code 8 (Light Motor)' },
  { value: 'Code 10', label: 'Code 10 (Heavy Motor)' },
  { value: 'Code 14', label: 'Code 14 (Extra Heavy)' },
  { value: 'Other',   label: 'Other' },
];

// Handles license photo + driver avatar uploads. All state is kept local to
// this dialog because the parent only cares about the resulting URLs on the
// formData object.
const DriverFormDialog = ({
  open, onOpenChange, formData, setFormData,
  selectedDriver, saving, onSubmit, vehicles = [],
}) => {
  const fileInputRef = useRef(null);
  const avatarInputRef = useRef(null);

  const uploadFile = async (file, endpoint, targetField, successMsg) => {
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    const response = await axios.post(`${API_URL}${endpoint}`, formDataUpload, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (response.data.success) {
      setFormData(prev => ({ ...prev, [targetField]: response.data.url }));
      toast.success(successMsg);
    }
  };

  const handleLicenseUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    if (!allowed.includes(file.type)) { toast.error('File must be JPEG, PNG, or PDF'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('File size must be less than 5MB'); return; }
    try {
      await uploadFile(file, '/api/upload/driver-license', 'license_photo_url', 'License photo uploaded');
    } catch { toast.error('Failed to upload photo'); }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
    if (!allowed.includes(file.type)) { toast.error('Driver photo must be JPEG, PNG, or WEBP'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('File size must be less than 5MB'); return; }
    try {
      await uploadFile(file, '/api/upload/driver-photo', 'photo_url', 'Driver photo uploaded');
    } catch { toast.error('Failed to upload driver photo'); }
  };

  const removeLicensePhoto = () => setFormData(prev => ({ ...prev, license_photo_url: '' }));
  const removeAvatar = () => setFormData(prev => ({ ...prev, photo_url: '' }));

  const avatarSrc = formData.photo_url
    ? (formData.photo_url.startsWith('http') ? formData.photo_url : `${API_URL}${formData.photo_url}`)
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {selectedDriver ? 'Edit Driver' : 'Add Driver'}
          </DialogTitle>
          <DialogDescription>
            {selectedDriver ? 'Update driver information' : 'Add a new driver to your fleet'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4 mt-4">
          {/* Driver Photo (Avatar) */}
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-slate-200 bg-slate-100 flex items-center justify-center flex-shrink-0" data-testid="driver-avatar-preview">
              {avatarSrc
                ? <img src={avatarSrc} alt="Driver" className="w-full h-full object-cover" />
                : <Users className="w-8 h-8 text-slate-400" />}
            </div>
            <div className="flex-1 space-y-2">
              <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Driver Photo</Label>
              <div className="flex gap-2">
                <input
                  type="file"
                  ref={avatarInputRef}
                  className="hidden"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handleAvatarUpload}
                  data-testid="driver-avatar-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => avatarInputRef.current?.click()}
                  data-testid="driver-avatar-upload-btn"
                >
                  <Upload className="w-3.5 h-3.5 mr-1.5" />
                  {formData.photo_url ? 'Change' : 'Upload photo'}
                </Button>
                {formData.photo_url && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={removeAvatar}
                    data-testid="driver-avatar-remove-btn"
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
              <p className="text-[11px] text-slate-500">Shown on the incident leaderboard and driver list.</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Full Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="John Smith"
                data-testid="driver-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>SA ID Number</Label>
              <Input
                value={formData.id_number}
                onChange={(e) => setFormData({ ...formData, id_number: e.target.value.replace(/\D/g, '').slice(0, 13) })}
                placeholder="8501015800088"
                className="font-data"
                maxLength={13}
                data-testid="driver-id-number-input"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Employee ID</Label>
              <Input
                value={formData.employee_id}
                onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                placeholder="EMP001"
                className="font-data"
                data-testid="driver-employee-id-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="john@company.com"
                data-testid="driver-email-input"
              />
            </div>
          </div>

          {/* Contact Numbers */}
          <div className="border-t pt-4">
            <h4 className="font-medium text-obsidian mb-3 flex items-center gap-2">
              <Phone className="w-4 h-4" />
              Contact Numbers
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Cell Phone 1 (Primary)</Label>
                <Input
                  value={formData.cell_phone_1}
                  onChange={(e) => setFormData({ ...formData, cell_phone_1: e.target.value })}
                  placeholder="+27 82 123 4567"
                  data-testid="driver-phone1-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Cell Phone 2 (Secondary)</Label>
                <Input
                  value={formData.cell_phone_2}
                  onChange={(e) => setFormData({ ...formData, cell_phone_2: e.target.value })}
                  placeholder="+27 83 987 6543"
                  data-testid="driver-phone2-input"
                />
              </div>
            </div>
          </div>

          {/* Next of Kin */}
          <div className="border-t pt-4">
            <h4 className="font-medium text-obsidian mb-3 flex items-center gap-2">
              <Users className="w-4 h-4" />
              Next of Kin / Emergency Contact
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Next of Kin Name</Label>
                <Input
                  value={formData.next_of_kin_name}
                  onChange={(e) => setFormData({ ...formData, next_of_kin_name: e.target.value })}
                  placeholder="Jane Smith"
                  data-testid="driver-nok-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Next of Kin Phone</Label>
                <Input
                  value={formData.next_of_kin_phone}
                  onChange={(e) => setFormData({ ...formData, next_of_kin_phone: e.target.value })}
                  placeholder="+27 84 555 1234"
                  data-testid="driver-nok-phone-input"
                />
              </div>
            </div>
          </div>

          {/* License */}
          <div className="border-t pt-4">
            <h4 className="font-medium text-obsidian mb-3">License Information</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>License Number</Label>
                <Input
                  value={formData.license_number}
                  onChange={(e) => setFormData({ ...formData, license_number: e.target.value })}
                  placeholder="ABC123456"
                  className="font-data"
                  data-testid="driver-license-input"
                />
              </div>
              <div className="space-y-2">
                <Label>License Type</Label>
                <Select
                  value={formData.license_type}
                  onValueChange={(value) => setFormData({ ...formData, license_type: value })}
                >
                  <SelectTrigger data-testid="driver-license-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {LICENSE_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2 mt-4">
              <Label>License Expiry Date</Label>
              <Input
                type="date"
                value={formData.license_expiry}
                onChange={(e) => setFormData({ ...formData, license_expiry: e.target.value })}
                data-testid="driver-license-expiry-input"
              />
            </div>

            {/* License Photo Upload */}
            <div className="space-y-2 mt-4">
              <Label>Driver&apos;s License Photo</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/jpg,application/pdf"
                onChange={handleLicenseUpload}
                className="hidden"
                data-testid="driver-license-photo-input"
              />
              {formData.license_photo_url ? (
                <div className="border rounded-lg p-3 bg-slate-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileImage className="w-5 h-5 text-green-600" />
                      <span className="text-sm text-slate-600">License photo uploaded</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(`${API_URL}${formData.license_photo_url}`, '_blank')}
                      >
                        View
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-500"
                        onClick={removeLicensePhoto}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="upload-license-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload License Photo
                </Button>
              )}
              <p className="text-xs text-slate-500">Accepts JPEG, PNG, or PDF (max 5MB)</p>
            </div>
          </div>

          <div className="border-t pt-4">
            <h4 className="font-medium text-obsidian mb-3">PDP (Professional Driving Permit)</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>PDP Number</Label>
                <Input
                  value={formData.pdp_number}
                  onChange={(e) => setFormData({ ...formData, pdp_number: e.target.value })}
                  placeholder="PDP123456"
                  className="font-data"
                  data-testid="driver-pdp-input"
                />
              </div>
              <div className="space-y-2">
                <Label>PDP Expiry</Label>
                <Input
                  type="date"
                  value={formData.pdp_expiry}
                  onChange={(e) => setFormData({ ...formData, pdp_expiry: e.target.value })}
                  data-testid="driver-pdp-expiry-input"
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Status</Label>
            <Select
              value={formData.status}
              onValueChange={(value) => setFormData({ ...formData, status: value })}
            >
              <SelectTrigger data-testid="driver-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Assigned Vehicle</Label>
            <Select
              value={formData.assigned_vehicle_id || 'none'}
              onValueChange={(v) => { if (!v) return; setFormData({ ...formData, assigned_vehicle_id: v === 'none' ? '' : v }); }}
            >
              <SelectTrigger data-testid="driver-assigned-vehicle-select"><SelectValue placeholder="No vehicle" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No vehicle</SelectItem>
                {vehicles.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.name} ({v.registration}){v.assigned_driver_name && v.assigned_driver_id !== selectedDriver?.id ? ` — now ${v.assigned_driver_name}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">One driver per vehicle — choosing a vehicle replaces its current driver.</p>
          </div>

          <div className="space-y-2">
            <Label>Notes</Label>
            <Input
              value={formData.notes || ''}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Optional notes"
              data-testid="driver-notes-input"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" className="bg-gray-800 hover:bg-gray-700" disabled={saving} data-testid="save-driver-btn">
              {saving ? 'Saving...' : (selectedDriver ? 'Update' : 'Add Driver')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default DriverFormDialog;
